'use client'

import { useTranslations } from 'next-intl'
import { JSX, useEffect, useRef } from 'react'

import TextInput, { TTextInputRef } from '../../../primitives/components/text-input'
import { SelectOptionType } from '../../../form/components/fields/select-field'
import Select, { TSelectRef } from '../../../primitives/components/select'
import { FilterOperators } from '../../../enums/filter-operators'

import { StyledPinnedFilters } from '../../styled/filter'
import { ColumnTypes, TFilter, TableColumn } from '../../types'
import { TFilterCollection } from '../../types/table-props'
import { generateAccessorColumn } from './filter'

const PINNED_FILTER_PREFIX = 'pinned-filter__'

export const isPinnedFilterKey = (key: string) => key.startsWith(PINNED_FILTER_PREFIX)

export type TResolvedPinnedFilter = {
    key: string
    column: string
    label?: string | JSX.Element
    type: ColumnTypes | 'select'
    operator: FilterOperators
    options: SelectOptionType[]
    defaultValue?: string | number | boolean
    placeholder?: string
}

// merges the column and its pinned filter config into everything needed to render and apply the filter
export const resolvePinnedFilter = <T,>(column: TableColumn<T>): TResolvedPinnedFilter => {
    const config = typeof column.pinnedFilter === 'object' ? column.pinnedFilter : undefined
    const accessor = generateAccessorColumn(column)
    const type = config?.type ?? column.type ?? 'text'

    return {
        key: PINNED_FILTER_PREFIX + accessor,
        column: config?.column ?? accessor,
        label: config?.label ?? column.label,
        type,
        operator: type === 'text' || type === 'select' ? 'ilike' : 'eq',
        options: config?.type === 'select' ? config.options : [],
        defaultValue: config?.defaultValue,
        placeholder: config?.placeholder,
    }
}

// inputs work with strings/numbers only, booleans are shown as a 'true' / 'false' select
const toInputValue = (value?: TFilter['value']) =>
    value === undefined || typeof value === 'number' ? value : String(value)

const toFilterValue = (type: TResolvedPinnedFilter['type'], value: string | number) => {
    if (type === 'number')
        return +value

    if (type === 'boolean')
        return value === 'true'

    return value
}

type TRenderPinnedFilterProps = {
    pinned: TResolvedPinnedFilter
    filter?: Partial<TFilter>
    updateFilter: (key: string, filter: TFilter) => void
    deleteFilter: (key: string) => void
}

const RenderPinnedFilter = ({ pinned, filter, updateFilter, deleteFilter }: TRenderPinnedFilterProps) => {
    const textInputRef = useRef<TTextInputRef>(null)
    const selectRef = useRef<TSelectRef>(null)
    const t = useTranslations('table.pinned-filters')
    const tRoot = useTranslations()

    const { key, column, type, operator, defaultValue } = pinned
    const isSelect = type === 'select' || type === 'boolean'
    const options: SelectOptionType[] =
        type === 'boolean'
            ? [
                { value: 'true', label: tRoot('yes') },
                { value: 'false', label: tRoot('no') },
            ]
            : pinned.options
    const inputValue = toInputValue(filter?.value ?? defaultValue)
    // filter seen by the previous run of the effect below; null before its first run
    const previousFilterRef = useRef<Partial<TFilter> | undefined | null>(null)

    // The filter was removed from outside (e.g. "clear filters") while the input still shows a value:
    // bring the input back to the configured default, or empty it. On mount this applies the default.
    // Text typed but not sent yet (the text input is debounced) has no filter either - that is not a removal,
    // otherwise another filter changing in the meantime would wipe the typed text.
    useEffect(() => {
        const previousFilter = previousFilterRef.current
        previousFilterRef.current = filter
        const removed = previousFilter === null || previousFilter !== undefined

        const currentRef = isSelect ? selectRef.current : textInputRef.current
        if (!currentRef?.hasValue() || filter || !removed)
            return

        if (defaultValue === undefined) {
            currentRef.clear()
            return
        }

        currentRef.setValue(toInputValue(defaultValue)!)
        updateFilter(key, { column, operator, value: defaultValue })
    }, [filter, defaultValue, column, key, operator, isSelect, updateFilter])

    const handleChange = (value?: string | number | null) => {
        if (value === undefined || value === null || value === '') {
            deleteFilter(key)
            return
        }

        updateFilter(key, { column, operator, value: toFilterValue(type, value) })
    }

    return (
        <div>
            <label>{pinned.label}</label>
            {isSelect ? (
                <Select
                    ref={selectRef}
                    options={options}
                    defaultValue={inputValue}
                    onChange={handleChange}
                    placeholder={t('select-placeholder')}
                    isClearable
                />
            ) : (
                <TextInput
                    ref={textInputRef}
                    allowClear
                    defaultValue={inputValue?.toString() ?? ''}
                    type={type}
                    placeholder={pinned.placeholder}
                    onValueChange={handleChange}
                />
            )}
        </div>
    )
}

type TProps = {
    pinnedFilters: TResolvedPinnedFilter[]
    filters: TFilterCollection
    updateFilter: (key: string, filter: TFilter) => void
    deleteFilter: (key: string) => void
}

const PinnedFilters = ({ pinnedFilters, filters, updateFilter, deleteFilter }: TProps) => {
    if (!pinnedFilters.length)
        return null

    return (
        <StyledPinnedFilters>
            {pinnedFilters.map(pinned => (
                <RenderPinnedFilter
                    key={pinned.key}
                    pinned={pinned}
                    filter={filters[pinned.key]}
                    updateFilter={updateFilter}
                    deleteFilter={deleteFilter}
                />
            ))}
        </StyledPinnedFilters>
    )
}

export default PinnedFilters
